import type { ToolCall } from '#models/ToolCallModel.js';
import type { IconName } from '@elara/client-core/ui-contracts/icon-name';

import type { ToolResultItem } from './types.js';

import { buildResultsLabel, findToolBadgeSpec, isHiddenToolName } from './registry.js';

/**
 * Platform-neutral decision of how a tool call renders as a badge for a regular viewer (the "found
 * N results" / plain-label view web shows non-privileged, non-debug users). Both the web `ToolBadge`
 * and the React Native `ToolBadge` render from this single source of truth — only the JSX differs.
 */
export type ToolBadgeContent =
  | { kind: 'hidden' }
  | { kind: 'plain'; icon: IconName; label: string; labelMarkdown: boolean; isActive: boolean }
  | {
      kind: 'expandable';
      icon: IconName;
      label: string;
      items: ToolResultItem[];
      dataSource?: string | readonly string[];
      isActive: boolean;
    };

/**
 * Decides how a tool call should render. Unknown/hidden tools are hidden; a spec with expandable
 * results (and at least one item) collapses to a "Found N X" summary; everything else falls back to
 * the spec's plain label. Mirrors the non-debug/non-privileged branch of the web `ToolBadge` exactly.
 */
export function resolveToolBadgeContent(toolCall: ToolCall): ToolBadgeContent {
  const spec = findToolBadgeSpec(toolCall.toolName);
  if (!spec || isHiddenToolName(toolCall.toolName)) return { kind: 'hidden' };

  const isActive = toolCall.isActive;
  const results =
    spec.extractResults && toolCall.extendedStatus !== 'failed' && toolCall.output !== undefined
      ? spec.extractResults(toolCall)
      : undefined;

  if (results && results.items.length > 0) {
    return {
      kind: 'expandable',
      icon: isActive ? 'progress_activity' : spec.icon,
      label: buildResultsLabel(spec, results.items.length, results.contextLabel, results.resultNoun),
      items: results.items,
      dataSource: spec.dataSource,
      isActive,
    };
  }

  return {
    kind: 'plain',
    icon: isActive ? 'progress_activity' : spec.icon,
    label: spec.label(toolCall, isActive),
    labelMarkdown: spec.labelMarkdown ?? false,
    isActive,
  };
}

/** Formats a data-source list into "Sourced from X[, Y] and Z" prose. Returns undefined when empty. */
export function formatToolDataSources(source: string | readonly string[] | undefined): string | undefined {
  if (!source) return undefined;
  const list = typeof source === 'string' ? [source] : source.filter((s) => s.length > 0);
  if (list.length === 0) return undefined;
  if (list.length === 1) return list[0];
  if (list.length === 2) return `${list[0]} and ${list[1]}`;
  const last = list.at(-1);
  return `${list.slice(0, -1).join(', ')}, and ${last}`;
}
